/**
 * useSavedFunnelResults
 *
 * Runs every saved funnel over the selected window so the Funnels list can
 * show conversion and visitors per row. One analysis request per funnel, the
 * same request FunnelStudio sends when a funnel is opened (sequential mode,
 * filter chips applied), cached under the `useFunnelAnalysis` key so other
 * views share it. At most 3 run at once (unlimited funnels on Scale); each
 * row fills in as its own result lands. A failed run leaves that row's
 * numbers empty; it is not reported here because opening the funnel reports
 * the same failure.
 */
import { useMemo } from "react"
import { useQueries } from "@tanstack/react-query"
import { useAuth } from "@/lib/contexts/AuthContext"
import { getFunnelAnalysis } from "@/lib/api/funnel-api"
import { isDemoSite, demoGenerators } from "@/lib/utils/demoData"
import { createLimiter } from "@/lib/utils/concurrencyLimit"
import {
  savedFunnelId,
  savedFunnelParams,
  summarizeFunnelResult,
  type FunnelRowResult,
} from "@/components/funnels/funnelListModel"

const runLimited = createLimiter(3)

export function useSavedFunnelResults({
  funnels,
  propertyId,
  window,
  filterFields,
  enabled = true,
}: {
  funnels: any[]
  propertyId?: string
  window: { start: string; end: string; timezone: string; granularity: string }
  filterFields?: Record<string, string | undefined>
  enabled?: boolean
}) {
  const { token } = useAuth()
  const filtersKey = JSON.stringify(filterFields ?? {})

  const results = useQueries({
    queries: funnels.map((funnel) => {
      const params = savedFunnelParams(funnel, window, propertyId, filterFields ?? {})
      const id = savedFunnelId(funnel) ?? funnel?.name
      return {
        // Same key shape as useFunnelAnalysis, so a funnel's run is shared.
        queryKey: params ? ["funnel-analysis", propertyId, params] : ["funnel-analysis", propertyId, "invalid", id, filtersKey],
        queryFn: async () => {
          if (!params || !propertyId || !token) return null
          if (isDemoSite(propertyId)) {
            return demoGenerators.funnelAnalysis(params.steps.map((s) => s.name))
          }
          return runLimited(() => getFunnelAnalysis(propertyId, params, token))
        },
        enabled: enabled && Boolean(params && propertyId && token),
        staleTime: 5 * 60 * 1000,
        retry: false,
        refetchOnWindowFocus: false,
      }
    }),
  })

  return useMemo(() => {
    const byId = new Map<string, FunnelRowResult | null>()
    const loadingIds = new Set<string>()
    funnels.forEach((funnel, i) => {
      const q = results[i]
      const id = savedFunnelId(funnel) ?? funnel?.name
      if (q?.isLoading) loadingIds.add(id)
      byId.set(id, summarizeFunnelResult(q?.data?.summary))
    })
    return { byId, loadingIds }
  }, [funnels, results])
}
